import type {
  EventMapDocument,
  EventMapGlobalElementView,
  EventMapSectionView,
  EventMapUnitView
} from '~/shared-kernel/presentation/event-map/event-map.types'

export interface EventMapDocumentNodeSource {
  id: string
  sectionId: string
  kind: 'seat' | 'group'
  saleTypeCode?: string | null | undefined
  available: boolean
  blocked?: boolean
  expandedFromGroup?: boolean
  individualSale?: boolean
  rowLabel?: string | null | undefined
  number?: string | null | undefined
  displayCode: string
  unitLabel?: string | null | undefined
  groupKind?: 'table' | 'box' | null | undefined
  label?: string | null | undefined
  capacity?: number | null | undefined
  markerStyle?: {
    shape?: 'circle' | 'square' | undefined
    size?: number | undefined
    fillColor: string
    strokeColor: string
    strokeWidth: number
  } | null | undefined
  x: number
  y: number
  width?: number | undefined
  height?: number | undefined
}

export interface EventMapDocumentSource {
  eventId: string
  localities: Array<{
    id: string
    name: string
    capacity: number
    available?: boolean
    showAvailability?: boolean
    selectableAsLocality?: boolean
    nodes: EventMapDocumentNodeSource[]
  }>
  sections: EventMapSectionView[]
  globalElements: EventMapGlobalElementView[]
}

export interface EventMapResponseDocumentSource {
  event: { id: string }
  sections: Array<Omit<EventMapSectionView, 'labelFontSize' | 'zone'> & {
    labelFontSize?: number
    zone?: EventMapSectionView['zone']
  }>
  globalElements: EventMapGlobalElementView[]
  localities: Array<{
    id: string
    name: string
    availableCapacity: number
    showAvailability?: boolean
    saleTypeCode?: string
    saleMode?: string
    nodes: Array<{
      expandedFromGroup?: boolean
      saleTypeCode?: string
      unitId: string
      sectionId: string
      code: string
      kind: 'seat' | 'group'
      groupKind?: 'table' | 'box' | null | undefined
      label?: string | null | undefined
      rowLabel?: string | null | undefined
      number?: string | null | undefined
      unitLabel?: string | null | undefined
      markerStyle?: EventMapDocumentNodeSource['markerStyle']
      capacity: number
      seatCapacity?: number | null | undefined
      posX: number
      posY: number
      width: number
      height: number
      status: 'available' | 'blocked' | 'held'
    }>
  }>
}

export function mapEventMapDocument(source: EventMapDocumentSource): EventMapDocument {
  const localityIds = new Set(source.localities.map(locality => locality.id))
  const visibleSections = source.sections.filter(section =>
    section.localityId === null || localityIds.has(section.localityId)
  )
  const sectionById = new Map(visibleSections.map(section => [section.id, section] as const))
  const nodesBySectionId = new Map<string, EventMapDocumentNodeSource[]>()

  for (const locality of source.localities) {
    for (const node of locality.nodes) {
      if (sectionById.get(node.sectionId)?.localityId !== locality.id) continue
      const nodes = nodesBySectionId.get(node.sectionId)
      if (nodes) nodes.push(node)
      else nodesBySectionId.set(node.sectionId, [node])
    }
  }

  return {
    eventId: source.eventId,
    localities: source.localities.map(({ id, name, capacity, available, showAvailability, selectableAsLocality }) => ({
      id,
      name,
      capacity,
      ...(available !== undefined ? { available } : {}),
      ...(showAvailability !== undefined ? { showAvailability } : {}),
      ...(selectableAsLocality !== undefined ? { selectableAsLocality } : {})
    })),
    sections: visibleSections.map(section => ({
      ...section,
      zone: section.zone ? { ...section.zone } : null,
      ...(section.points ? { points: section.points.map(point => ({ ...point })) } : {})
    })),
    globalElements: source.globalElements.map(element => ({ ...element })),
    seatLayouts: Object.fromEntries(visibleSections
      .filter(section => section.localityId && localityIds.has(section.localityId))
      .map(section => [section.id, {
        sectionId: section.id,
        units: (nodesBySectionId.get(section.id) ?? []).map((node): EventMapUnitView =>
          node.kind === 'seat'
            ? {
                id: node.id,
                kind: 'seat',
                available: node.available,
                ...(node.saleTypeCode ? { saleTypeCode: node.saleTypeCode } : {}),
                ...(node.blocked !== undefined ? { blocked: node.blocked } : {}),
                expandedFromGroup: node.expandedFromGroup ?? false,
                individualSale: node.individualSale ?? false,
                rowLabel: node.rowLabel ?? null,
                number: node.number ?? node.displayCode,
                displayCode: node.displayCode,
                ...(node.capacity ? { capacity: node.capacity } : {}),
                ...(node.unitLabel ? { unitLabel: node.unitLabel } : {}),
                ...(node.markerStyle?.shape && node.markerStyle.size
                  ? { markerStyle: {
                      shape: node.markerStyle.shape,
                      size: node.markerStyle.size,
                      fillColor: node.markerStyle.fillColor,
                      strokeColor: node.markerStyle.strokeColor,
                      strokeWidth: node.markerStyle.strokeWidth
                    } }
                  : {}),
                x: node.x,
                y: node.y
              }
            : {
                id: node.id,
                kind: 'group',
                available: node.available,
                ...(node.saleTypeCode ? { saleTypeCode: node.saleTypeCode } : {}),
                ...(node.blocked !== undefined ? { blocked: node.blocked } : {}),
                expandedFromGroup: node.expandedFromGroup ?? false,
                individualSale: node.individualSale ?? false,
                groupKind: node.groupKind ?? 'table',
                ...(node.unitLabel ? { unitLabel: node.unitLabel } : {}),
                label: node.label ?? node.displayCode,
                capacity: node.capacity ?? 1,
                width: node.width ?? 20,
                height: node.height ?? 20,
                ...(node.markerStyle ? { markerStyle: {
                  ...(node.markerStyle.shape ? { shape: node.markerStyle.shape } : {}),
                  ...(node.markerStyle.size ? { size: node.markerStyle.size } : {}),
                  fillColor: node.markerStyle.fillColor,
                  strokeColor: node.markerStyle.strokeColor,
                  strokeWidth: node.markerStyle.strokeWidth
                } } : {}),
                x: node.x,
                y: node.y
              })
      }]))
  }
}

export function mapEventMapResponseDocument(source: EventMapResponseDocumentSource) {
  return mapEventMapDocument({
    eventId: source.event.id,
    localities: source.localities.map(locality => ({
      id: locality.id,
      name: locality.name,
      capacity: locality.availableCapacity,
      available: locality.availableCapacity > 0,
      showAvailability: locality.showAvailability === true,
      selectableAsLocality: locality.saleMode === 'general-admission',
      nodes: locality.saleMode === 'general-admission'
        ? []
        : locality.nodes.map(node => ({
        id: node.unitId,
        sectionId: node.sectionId,
        kind: node.kind,
        available: node.status === 'available',
        saleTypeCode: node.saleTypeCode ?? locality.saleTypeCode,
        expandedFromGroup: node.expandedFromGroup ?? false,
        individualSale: (node.saleTypeCode ?? locality.saleTypeCode) === 'INDIVIDUAL'
          && (node.kind === 'group' || (node.seatCapacity ?? node.capacity) > 1 || locality.saleMode === 'quantity-per-unit'),
        rowLabel: node.rowLabel,
        number: node.number ?? node.code,
        displayCode: node.code,
        unitLabel: node.unitLabel,
        groupKind: node.groupKind,
        label: node.label,
        capacity: node.seatCapacity ?? node.capacity,
        markerStyle: node.markerStyle,
        x: node.posX,
        y: node.posY,
        width: node.width,
        height: node.height
      }))
    })),
    sections: source.sections.map(section => ({
      ...section,
      labelFontSize: section.labelFontSize ?? 14,
      zone: section.zone ?? null
    })),
    globalElements: source.globalElements
  })
}
